import { Container, Graphics, Rectangle, Sprite } from 'pixi.js';
import { Scene } from '@/core/Scene';
import { Palette } from '@/config/palette';
import { makeText, makeTitle } from '@/ui/text';
import { art } from '@/platform/art';

/** Splash / title screen. Key-art backdrop; tap anywhere to enter the hub. */
export class BootScene extends Scene {
  private group = new Container();
  private bg: Sprite | null = null;
  private readonly overlay = new Graphics();
  private prompt = makeText('НАЖМИ, ЧТОБЫ НАЧАТЬ', {
    size: 16,
    color: Palette.text,
    weight: '700',
    letterSpacing: 1.5,
    dropShadow: true,
  });
  private glow = new Graphics();
  private t = 0;

  enter(): void {
    const tex = art('keyart');
    if (tex) {
      this.bg = new Sprite(tex);
      this.bg.anchor.set(0.5);
      this.root.addChild(this.bg);
    }
    this.root.addChild(this.overlay, this.group);

    const sub = makeText('M Y T H I C +   L I T E', {
      size: 13,
      color: Palette.gold,
      weight: '700',
      letterSpacing: 4,
    });
    const title = makeTitle('ПОДЗЕМЕЛЬЕ', 40, Palette.text);
    const title2 = makeTitle('БЕСКОНЕЧНОСТИ', 30, Palette.arcane);

    this.group.addChild(this.glow, sub, title, title2, this.prompt);

    this.root.eventMode = 'static';
    this.root.hitArea = new Rectangle(0, 0, this.width, this.height);
    this.root.on('pointertap', () => {
      void import('./MainMenuScene').then((m) =>
        this.game.scenes.change((g) => new m.MainMenuScene(g)),
      );
    });

    this.layout();
  }

  private layout(): void {
    const w = this.width;
    const h = this.height;
    const cx = w / 2;

    // cover-fit the key art
    if (this.bg) {
      const tex = this.bg.texture;
      const s = Math.max(w / tex.width, h / tex.height);
      this.bg.scale.set(s);
      this.bg.position.set(cx, h / 2);
    }
    // darken for text legibility — gentle overall + a stronger band top & bottom
    this.overlay.clear();
    this.overlay.rect(0, 0, w, h).fill({ color: 0x05040a, alpha: 0.28 });
    this.overlay.rect(0, h * 0.62, w, h * 0.38).fill({ color: 0x05040a, alpha: 0.45 });
    this.overlay.rect(0, 0, w, this.insets.top + 70).fill({ color: 0x05040a, alpha: 0.4 });

    // brand at top, title block over the dimmed lower third
    const [sub, title, title2] = this.group.children.slice(1);
    sub.position.set(cx, this.insets.top + 48);
    title.position.set(cx, h * 0.7);
    title2.position.set(cx, h * 0.77);
    this.prompt.position.set(cx, h - this.insets.bottom - 70);

    this.glow.clear();
    this.glow.circle(cx, h * 0.735, 150).fill({ color: Palette.arcane, alpha: 0.1 });
  }

  override update(dt: number): void {
    this.t += dt;
    this.prompt.alpha = 0.5 + 0.45 * (0.5 + 0.5 * Math.sin(this.t * 3));
    this.glow.scale.set(1 + 0.03 * Math.sin(this.t * 1.5));
  }

  override resize(): void {
    this.root.hitArea = new Rectangle(0, 0, this.width, this.height);
    this.layout();
  }
}
